import { useRef, useState } from "react";
import { ClipboardText, UploadSimple, Trash, CircleNotch, FileArrowDown, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { openChecklistPdf, emailChecklist } from "@/lib/report";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const DOC_CHECKLIST = [
  {
    id: "A", title: "Engagement & Scope (IVS 101 / RV Rules)", items: [
      { key: "a1", text: "Letter of engagement / work order from the bank — purpose, valuation date and basis of value." },
      { key: "a2", text: "Borrower / owner details, plus a contact person for the site visit." },
      { key: "a3", text: "Independence & no-conflict declaration (your own, kept on file)." },
    ],
  },
  {
    id: "B", title: "Title & Ownership", items: [
      { key: "b1", text: "Registered sale / gift / partition deed or will, with chain of title (13–30 years as the bank requires)." },
      { key: "b2", text: "Mutation (Namantaran) order in the current owner's name." },
      { key: "b3", text: "Legal Search Report / Title Investigation Report from the bank's panel advocate." },
      { key: "b4", text: "Encumbrance search from the Sub-Registrar + CERSAI search for existing charges." },
      { key: "b5", text: "Power of attorney, if anyone is signing on the owner's behalf." },
    ],
  },
  {
    id: "C", title: "Land Revenue Records (CG-specific)", items: [
      { key: "c1", text: "B-1 (Kishtbandi Khatauni) and P-II (Khasra Panchsala) for the current year, from Bhuiyan." },
      { key: "c2", text: "Khasra Naksha (village map) showing the plot's location and boundaries." },
      { key: "c3", text: "Diversion order under Sec. 172 of the CG Land Revenue Code, for non-agricultural use." },
      { key: "c4", text: "Demarcation (Seemankan) report, if boundaries are disputed or unclear." },
    ],
  },
  {
    id: "D", title: "Statutory Approvals", items: [
      { key: "d1", text: "Sanctioned building plan (Municipal Corp. / Nagar Palika / Gram Panchayat) with permission letter." },
      { key: "d2", text: "Layout / T&CP approval for colony or plotted land, and the colony-development licence." },
      { key: "d3", text: "RERA registration, for projects and flats." },
      { key: "d4", text: "Completion / occupancy certificate — if absent, flag it as a deviation risk." },
      { key: "d5", text: "NOCs where they apply: fire, airport height, environment, Nazul." },
    ],
  },
  {
    id: "E", title: "Taxes & Utilities (proof of possession)", items: [
      { key: "e1", text: "Latest property tax receipt." },
      { key: "e2", text: "Latest electricity and water bills." },
      { key: "e3", text: "Land revenue (Lagaan) receipt, for rural or diverted land." },
    ],
  },
  {
    id: "F", title: "Technical & Site", items: [
      { key: "f1", text: "Site plan with dimensions and boundaries on all four sides, checked against the deed." },
      { key: "f2", text: "Measurement sheet + built-up area statement (BUA, carpet area, FAR used vs permitted)." },
      { key: "f3", text: "Geo-tagged photographs, with latitude and longitude." },
      { key: "f4", text: "Year of construction, specifications and structure type, for depreciation." },
    ],
  },
  {
    id: "G", title: "Market Evidence", items: [
      { key: "g1", text: "Collector Guideline Rate for the relevant year (Panjiyan portal)." },
      { key: "g2", text: "Comparable sale instances: registered deeds or Sub-Registrar data." },
      { key: "g3", text: "Local enquiry notes (broker / market rates), recorded with source and date." },
    ],
  },
  {
    id: "H", title: "Additional Documents by Property Type", items: [
      { key: "h1", text: "Flats: agreement for sale, builder's NOC for mortgage, allotment letter, society share certificate, tripartite agreement if under construction." },
      { key: "h2", text: "Leasehold land (CSIDC / NRANVP / Nazul): lease deed, lessor's NOC / permission to mortgage, lease rent receipts, remaining lease term." },
      { key: "h3", text: "Industrial units: CSIDC allotment, factory licence, CECB Consent to Establish & Operate, fixed asset register, plant & machinery invoices." },
      { key: "h4", text: "Agricultural land: check whether it falls under Sec. 165(6) restrictions on tribal (adivasi) land transfer." },
    ],
  },
];

const STATUSES = ["Pending", "Collected", "Not Applicable", "Not Available"];
const STATUS_CLASS = {
  "Collected": "text-green-700 border-green-300 bg-green-50",
  "Not Applicable": "text-slate-600 border-slate-300 bg-slate-50",
  "Pending": "text-amber-700 border-amber-300 bg-amber-50",
  "Not Available": "text-red-700 border-red-300 bg-red-50",
};

const ALL_KEYS = DOC_CHECKLIST.flatMap((s) => s.items.map((i) => i.key));

export default function DocsChecklistTab({ value, onChange, jobId, reference, onPersist }) {
  const data = value || {};
  const [exporting, setExporting] = useState(false);
  const doDownload = async () => {
    if (!jobId) return;
    setExporting(true);
    try { if (onPersist) await onPersist(); await openChecklistPdf(jobId, reference); }
    finally { setExporting(false); }
  };
  const doEmail = async () => {
    if (!jobId) return;
    setExporting(true);
    try { if (onPersist) await onPersist(); await emailChecklist(jobId); }
    finally { setExporting(false); }
  };
  const setItem = (key, upd) => onChange({ ...data, [key]: { ...(data[key] || {}), ...upd } });
  const fileRefs = useRef({});
  const [uploading, setUploading] = useState({});
  const uploadShot = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!jobId) { toast.error("Create the job first (upload a document in the Docs tab)."); if (e.target) e.target.value = ""; return; }
    setUploading((m) => ({ ...m, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post(`/jobs/${jobId}/upload-image`, fd);
      setItem(key, { screenshot: res.path });
      toast.success(/\.pdf$/i.test(res.path) ? "PDF attached" : "Screenshot attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading((m) => ({ ...m, [key]: false })); if (e.target) e.target.value = ""; }
  };
  const setSection = (section, status) => {
    const next = { ...data };
    section.items.forEach((i) => { next[i.key] = { ...(next[i.key] || {}), status }; });
    onChange(next);
  };
  const statusOf = (key) => (data[key]?.status) || "Pending";
  const counts = ALL_KEYS.reduce((acc, k) => { const s = statusOf(k); acc[s] = (acc[s] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="docs-checklist-tab">
      <div>
        <Label className="text-sm font-semibold flex items-center gap-1.5"><ClipboardText size={16} /> Essential Documents Checklist</Label>
        <p className="text-[11px] text-muted-foreground mt-0.5">Track collection & diligence of every supporting document. Set a status and add remarks — the full checklist prints as an annexure in the valuation report.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="checklist-export-bar">
        <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-8" disabled={!jobId || exporting} title={!jobId ? "Create the job first (Docs tab)" : ""} onClick={doDownload} data-testid="checklist-download-btn">
          {exporting ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <FileArrowDown size={14} className="mr-1" />} Download checklist PDF
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-8" disabled={!jobId || exporting} title={!jobId ? "Create the job first (Docs tab)" : ""} onClick={doEmail} data-testid="checklist-email-btn">
          <EnvelopeSimple size={14} className="mr-1" /> Email to bank
        </Button>
        <span className="text-[10px] text-muted-foreground">Built from saved data (your entries are saved first).</span>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px]" data-testid="checklist-summary">
        {STATUSES.map((s) => (
          <span key={s} className={`px-2 py-0.5 rounded-full border font-medium ${STATUS_CLASS[s]}`} data-testid={`checklist-count-${s.replace(/\s+/g, "-").toLowerCase()}`}>
            {s}: {counts[s] || 0}
          </span>
        ))}
        <span className="px-2 py-0.5 rounded-full border border-primary/30 bg-primary/5 text-primary font-medium">Total: {ALL_KEYS.length}</span>
      </div>

      {DOC_CHECKLIST.map((section) => (
        <div key={section.id} className="rounded-lg border bg-background" data-testid={`checklist-section-${section.id}`}>
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/30">
            <div className="text-xs font-semibold text-primary">{section.id}. {section.title}</div>
            <select
              className="h-7 rounded-md border border-border bg-background px-2 text-[11px] text-muted-foreground"
              value=""
              onChange={(e) => { if (e.target.value) setSection(section, e.target.value); }}
              data-testid={`checklist-section-bulk-${section.id}`}
            >
              <option value="">Set all…</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="divide-y">
            {section.items.map((item) => {
              const status = statusOf(item.key);
              return (
                <div key={item.key} className="px-3 py-2 space-y-1.5" data-testid={`checklist-item-${item.key}`}>
                  <div className="flex items-start gap-2">
                    <p className="flex-1 text-xs leading-snug">{item.text}</p>
                    <select
                      className={`h-7 shrink-0 rounded-md border px-2 text-[11px] font-medium ${STATUS_CLASS[status]}`}
                      value={status}
                      onChange={(e) => setItem(item.key, { status: e.target.value })}
                      data-testid={`checklist-status-${item.key}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <Input
                    value={data[item.key]?.remarks || ""}
                    onChange={(e) => setItem(item.key, { remarks: e.target.value })}
                    placeholder="Remarks / reference (optional)"
                    className="h-7 text-[11px]"
                    data-testid={`checklist-remarks-${item.key}`}
                  />
                  <div className="flex items-center gap-2">
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.pdf" className="hidden" ref={(el) => (fileRefs.current[item.key] = el)} onChange={(e) => uploadShot(item.key, e)} data-testid={`checklist-shot-input-${item.key}`} />
                    {data[item.key]?.screenshot ? (
                      <>
                        {/\.pdf($|\?)/i.test(data[item.key].screenshot) ? (
                          <a href={`${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(data[item.key].screenshot)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-10 px-2 rounded border bg-muted/40 text-[11px] text-primary hover:underline" data-testid={`checklist-shot-thumb-${item.key}`}>
                            <FileArrowDown size={14} /> View PDF
                          </a>
                        ) : (
                          <a href={`${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(data[item.key].screenshot)}`} target="_blank" rel="noreferrer">
                            <img src={`${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(data[item.key].screenshot)}`} alt="proof" className="h-10 w-16 object-cover rounded border" data-testid={`checklist-shot-thumb-${item.key}`} />
                          </a>
                        )}
                        <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px] text-destructive" onClick={() => setItem(item.key, { screenshot: "" })} data-testid={`checklist-shot-remove-${item.key}`}><Trash size={12} className="mr-1" /> Remove</Button>
                      </>
                    ) : (
                      <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] rounded-full" disabled={uploading[item.key] || !jobId} title={!jobId ? "Create the job first (Docs tab)" : ""} onClick={() => fileRefs.current[item.key]?.click()} data-testid={`checklist-shot-btn-${item.key}`}>
                        {uploading[item.key] ? <CircleNotch size={12} className="mr-1 animate-spin" /> : <UploadSimple size={12} className="mr-1" />} {uploading[item.key] ? "Uploading…" : "Attach image / PDF"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
